import { useState, useEffect } from "react";
import { Paperclip, Camera, X as XIcon, ShieldCheck } from "lucide-react";
import Modal from "../components/Modal";
import GuideBanner from "../components/GuideBanner";
import Field, { inputStyle, selectStyle, inputErrorStyle } from "../components/Field";
import { CONTACT_CHANNELS } from "../constants";
import { exigeComprovante, diasEntre } from "../lib/abordagem";
import { lerArquivo, resumo as resumoConversa, descricao as descreverConversa, mesclar } from "../lib/conversaExport";
import { uploadToStorage, getSignedUrl, ehImagem, reduzirFoto } from "../lib/storage";
import { generateId, getToday, maskPhone, addMonths } from "../utils";
import CameraCaptura, { cameraNoSiteDisponivel } from "../components/CameraCaptura";
import { lerRascunho, gravarRascunho, apagarRascunho } from "../lib/rascunho";

export const RASCUNHO_INTERACAO = "registrar-contato";

// Data e hora de agora no fuso de quem está usando — o campo datetime-local
// não aceita ISO em UTC, e o horário do contato é o do vendedor, não o do servidor.
const agoraLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

const EXT = { "image/jpeg": "jpg", "image/jpg": "jpg", "image/png": "png", "image/webp": "webp", "image/heic": "heic", "application/pdf": "pdf", "application/json": "json" };

const emptyData = () => ({
  channel: "WhatsApp", note: "", nextAction: "", nextActionDate: "",
  contactName: "", phoneCelular: "", phoneFixo: "",
  hadCall: false, spokeWith: "",
  feedback: "",
  noInterest: false,
  motivoRecusa: "",
  resumeDate: "",
  immediateReturn: "",
  nextAttemptDate: "",
  acceptedCredential: false,
  contatoEm: agoraLocal(),
  comprovantePath: "", comprovanteNome: "", comprovanteTipo: "",
  conversaResumo: null,
  // A conversa exportada, inteira e já mesclada — guardada para juntar o próximo
  // export por cima (dedup) e para subir o consolidado no lugar do último.
  conversa: null,
  motivoSemPrint: "",
  resultado: "",
  // A conversa continua em OUTRO número: quem atendeu mandou falar com o dono,
  // com o financeiro, com o WhatsApp da matriz. Sem um lugar para isso, o
  // número novo virava texto solto na descrição e o próximo contato saía do
  // mesmo número que já tinha dito para procurar outro.
  continuaEm: "", continuaCom: "",
  // Registro telefônico: o número para onde a ligação saiu. Ligação não tem
  // export — é o número, com o nome de quem registrou, que responde por ela.
  telefoneLigacao: "",
});

export default function NewInteraction({
  target, onClose, clients, setClients, partners, setPartners, setTasks, userId, userName = "",
  onAceitouCredenciar,
}) {
  const [data, setData] = useState(emptyData());
  // O botão "Aceitou" do card não move mais o estágio sozinho: abre este
  // registro já marcado em "Aceitou credenciar", e o avanço passa pela prova.
  useEffect(() => {
    if (!target) return;
    // Rascunho deste mesmo contato (a aba recarregou no meio do registro).
    const r = lerRascunho(RASCUNHO_INTERACAO);
    if (r?.target?.id === target.id && r?.target?.type === target.type && r.data) {
      setData({ ...emptyData(), ...r.data });
      return;
    }
    if (target?.resultado) setData((p) => ({ ...p, resultado: target.resultado }));
  }, [target]);
  useEffect(() => {
    if (!target) return;
    const mexeu = data.note || data.comprovantePath || data.contactName || data.telefoneLigacao || data.resultado;
    // A conversa inteira fica de fora (pode ser grande); o anexo já está no servidor.
    if (mexeu) gravarRascunho(RASCUNHO_INTERACAO, { target: { type: target.type, id: target.id, taskId: target.taskId }, data: { ...data, conversa: null } });
  }, [target, data]);
  const [errors, setErrors] = useState({});
  const [enviandoPrint, setEnviandoPrint] = useState(false);
  const [camera, setCamera] = useState(false);

  const isPartner = target?.type === "partner";
  const isClient = target?.type === "client";

  const entity = isClient
    ? clients?.find((c) => c.id === target?.id)
    : partners?.find((p) => p.id === target?.id);

  const printObrigatorio = isPartner && exigeComprovante(data.channel);
  // ── O que faz um contato AVANÇAR o funil ───────────────────────────
  // Regra de 18/09/2026: para andar na Rede é preciso o EXPORT da conversa do
  // WhatsApp — print não basta, porque não traz a conversa inteira nem hora
  // verificável — ou o registro telefônico com o número chamado. Os outros
  // canais (visita, e-mail, indicação) continuam sendo registrados, mas NÃO
  // mudam o estágio: ficam na trilha como contato, e o avanço espera a prova.
  const porTelefone = isPartner && data.channel === "Telefone";
  const temExport = data.comprovanteTipo === "conversa" && !!data.comprovantePath;
  // Visita presencial (primeiro contato na porta, sem mensagem antes): avança
  // com foto da visita + nome de quem recebeu. Sem isso, quem visitava tinha de
  // registrar como ligação para o card andar.
  const porVisita = isPartner && data.channel === "Visita Presencial";
  const temFotoVisita = !!data.comprovantePath && data.comprovanteTipo === "print" && /\.(jpe?g|png|webp|heic|heif)$/i.test(data.comprovantePath);
  const provaVisita = porVisita && temFotoVisita && !!data.contactName.trim();
  const provaAvanco = isPartner && ((data.channel === "WhatsApp" && temExport) || (porTelefone && (data.telefoneLigacao || "").replace(/\D/g, "").length >= 10) || provaVisita);

  // O print vai direto para o Storage (mesmo bucket privado das fichas) e o
  // registro guarda só o caminho — a imagem nunca trafega dentro da linha.
  const anexarPrint = async (file) => {
    if (!file) return;
    setEnviandoPrint(true);
    setErrors((p) => ({ ...p, comprovante: "" }));

    const ehConversa = /json$/i.test(file.name || "") || file.type === "application/json";

    // ── Conversa exportada da plataforma ──────────────────────────
    // Vale mais que print: traz a conversa inteira, com hora real, e preenche a
    // data sozinho. Se JÁ existe um export anexado, este NOVO é juntado por cima
    // — a plataforma exporta a conversa toda a cada vez, então mesclar dedup as
    // linhas repetidas e refaz os totais.
    if (ehConversa) {
      const r = await lerArquivo(file);
      if (!r.ok) {
        setErrors((p) => ({ ...p, comprovante: r.erro }));
        setEnviandoPrint(false);
        return;
      }
      const merged = data.conversa ? mesclar([data.conversa, r.conversa]) : r.conversa;
      const res = resumoConversa(merged);
      const path = `contatos/${target?.id || "sem-id"}-${generateId().slice(0, 8)}.json`;
      // Um export só sobe COMO VEIO — preserva a assinatura da plataforma. Dois
      // ou mais sobem o CONSOLIDADO (dedup): sem isso a análise leria só o
      // último arquivo, e a contagem voltaria a divergir.
      const conteudo = merged.mesclado ? new Blob([JSON.stringify(merged)], { type: "application/json" }) : file;
      const nome = merged.mesclado ? `conversa-consolidada-${res.fontes}-exports.json` : (file.name || "conversa.json");
      try {
        await uploadToStorage(path, conteudo, "application/json");
        setData((p) => {
          // Refaz a nota automática quando o total muda; nota escrita à mão fica.
          const notaAuto = !p.note || /^Conversa exportada/.test(p.note);
          // O export diz com quem a conversa foi do lado do fornecedor/cliente
          // (nome do contato no WhatsApp e o número). Preenche o que estiver
          // vazio — o que a pessoa já digitou não é sobrescrito.
          const nomeDoOutroLado = res.contato && res.contato !== "—" ? res.contato : "";
          return {
            ...p,
            contactName: p.contactName || nomeDoOutroLado,
            phoneCelular: p.phoneCelular || (res.numero ? maskPhone(String(res.numero).replace(/^55(?=\d{10,11}$)/, "")) : ""),
            comprovantePath: path,
            comprovanteNome: nome,
            comprovanteTipo: "conversa",
            conversa: merged,
            conversaResumo: res,
            contatoEm: res.contatoEm || p.contatoEm,
            note: notaAuto ? `Conversa exportada — ${descreverConversa(res)}.` : p.note,
          };
        });
      } catch (err) {
        setErrors((p) => ({ ...p, comprovante: "Não consegui enviar o arquivo: " + (err.message || "tente de novo") }));
      }
      setEnviandoPrint(false);
      return;
    }

    // ── Print / foto / PDF ────────────────────────────────────────
    // Um anexo só, substitui o anterior (não existe "juntar" imagem).
    // Foto de câmera é reduzida antes de subir (5–12 MB no tablet, HEIC no iPad).
    if (ehImagem(file)) file = await reduzirFoto(file);
    const extNome = file.name?.includes(".") ? file.name.split(".").pop().toLowerCase().replace(/[^a-z0-9]/g, "") : "";
    const ext = EXT[(file.type || "").toLowerCase()] || extNome || "jpg";
    const path = `contatos/${target?.id || "sem-id"}-${generateId().slice(0, 8)}.${ext}`;
    try {
      await uploadToStorage(path, file, file.type || "image/jpeg");
      setData((p) => ({
        ...p,
        comprovantePath: path,
        comprovanteNome: file.name || `comprovante.${ext}`,
        comprovanteTipo: "print",
        conversa: null,
        conversaResumo: null,
      }));
    } catch (err) {
      setErrors((p) => ({ ...p, comprovante: "Não consegui enviar o arquivo: " + (err.message || "tente de novo") }));
    }
    setEnviandoPrint(false);
  };

  const verPrint = async () => {
    const win = window.open("", "_blank");
    const url = await getSignedUrl(data.comprovantePath);
    if (!url) { if (win) win.close(); return; }
    if (win) win.location.href = url; else window.open(url, "_blank");
  };

  const validate = () => {
    const err = {};
    if (!data.note.trim()) err.note = "Descreva o que aconteceu neste contato";
    // "Sem interesse" sem motivo é um beco: daqui a três meses a retomada
    // começa do zero e o "não" de hoje não ensinou nada a ninguém.
    if (data.noInterest && !data.motivoRecusa?.trim()) {
      err.motivoRecusa = "Diga o motivo — é o que vai orientar a retomada.";
    }
    // Contato por WhatsApp sem print era palavra contra palavra: o card dizia
    // "abordagem" e ninguém conseguia saber se a conversa existiu.
    if (printObrigatorio && !temExport) {
      err.comprovante = data.comprovantePath
        ? "Print não basta para contato por WhatsApp: exporte a conversa na plataforma e anexe o arquivo. Se foi ligação, troque o canal para Telefone."
        : "Anexe o export da conversa do WhatsApp. Se foi ligação, troque o canal para Telefone e informe o número.";
    }
    if (porTelefone && (data.telefoneLigacao || "").replace(/\D/g, "").length < 10) {
      err.telefoneLigacao = "Informe o número para onde você ligou (com DDD).";
    }
    // Nos outros canais o anexo não trava (não existe print de visita), mas
    // registrar sem prova nenhuma exige dizer por quê. Sem isso, "Presencial"
    // vira a porta dos fundos para escapar do comprovante.
    if (isPartner && !printObrigatorio && !porTelefone && !data.comprovantePath && !data.motivoSemPrint?.trim()) {
      err.motivoSemPrint = "Sem anexo, diga por que não há comprovante deste contato.";
    }
    if (isPartner && !data.contatoEm) err.contatoEm = "Informe a data e a hora do contato";
    // Conversa anexada sem desfecho é registro pela metade: a fila do gerente
    // encheria de "aconteceu alguma coisa" sem dizer o quê, e o funil não
    // andaria. Aguardando resposta TAMBÉM é desfecho — e precisa de data.
    if (isPartner && data.comprovanteTipo === "conversa" && !data.resultado) {
      err.resultado = "Diga no que deu esta conversa.";
    }
    if (data.resultado === "aguardando" && !data.nextAttemptDate) {
      err.nextAttemptDate = "Quando você tenta de novo?";
    }
    if (data.continuaEm && data.continuaEm.replace(/\D/g, "").length < 10) {
      err.continuaEm = "Número incompleto — informe com DDD.";
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = () => {
    if (!validate() || !target) return;
    const today = getToday();
    const { type, id } = target;

    // O contato vale no dia em que ACONTECEU, não no dia em que foi digitado.
    // Conversa exportada na sexta e registrada na segunda contava para a meta
    // de segunda — a de sexta ficava em débito, e a pessoa era cobrada por um
    // dia que ela trabalhou. A data sai de `contatoEm`, que a conversa já
    // preenche com a última mensagem.
    const diaDoContato = (data.contatoEm || "").slice(0, 10) || today;
    // A meta conta pelo dia do LANÇAMENTO (`date`/`today` — mudar o número de um
    // dia já fechado seria pior que a imprecisão), mas o dia real da conversa não
    // se perde: fica em `diaDaConversa`, e a diferença vira badge para gerência
    // ler o número com contexto ("os de hoje são da semana passada").
    const diaDaConversa = diaDoContato;
    const diasDeAtraso = Math.max(0, diasEntre(diaDaConversa, today));

    // O bloco de resultado é a forma nova de responder o que o formulário já
    // perguntava em três lugares. Ele ALIMENTA os campos antigos em vez de
    // criar um segundo caminho — dois jeitos de dizer a mesma coisa acabariam
    // discordando.
    const aceitou = data.resultado === "aceitou" || data.acceptedCredential;
    const semInteresse = data.resultado === "sem_interesse" || data.noInterest;
    const retorno = data.resultado === "aguardando" ? "aguardando"
      : data.resultado === "sem_retorno" ? "sem_retorno"
      : data.resultado === "aceitou" ? "sim"
      : data.immediateReturn;


    const interaction = {
      // id próprio: é por ele que a conferência do gerente aponta para este
      // contato sem depender da posição na lista.
      id: generateId(),
      date: today, registradoEm: today, diaDaConversa, diasDeAtraso, channel: data.channel, note: data.note,
      contactName: data.contactName, phoneCelular: data.phoneCelular,
      phoneFixo: data.phoneFixo, hadCall: data.hadCall, spokeWith: data.spokeWith,
      feedback: data.feedback, noInterest: semInteresse, motivoRecusa: data.motivoRecusa || "",
      immediateReturn: retorno, resultado: data.resultado || "",
      // A prova do contato: caminho do print no Storage e o momento informado.
      contatoEm: data.contatoEm || "", comprovantePath: data.comprovantePath || "",
      comprovanteNome: data.comprovanteNome || "", comprovado: !!data.comprovantePath,
      comprovanteTipo: data.comprovanteTipo || (data.comprovantePath ? "print" : ""),
      conversaResumo: data.conversaResumo || null,
      autor: userName || "", userId,
      motivoSemPrint: data.comprovantePath ? "" : (data.motivoSemPrint || ""),
      // Registro telefônico: o número chamado responde pelo contato.
      telefoneLigacao: porTelefone ? data.telefoneLigacao : "",
      // Avançou o funil? Só com export (WhatsApp) ou número (ligação).
      provaAvanco,
      // Onde a conversa continua, quando ela mudou de número.
      continuacao: data.continuaEm
        ? { telefone: data.continuaEm, nome: data.continuaCom || "", de: entity?.phone || "" }
        : null,
      // Onde este contato aconteceu, gravado no próprio contato. Se a empresa
      // mudar de endereço depois, o relatório de hoje continua verdadeiro.
      uf: entity?.region || "", cidade: entity?.cidade || "",
    };

    if (type === "client") {
      setClients((p) => p.map((c) => {
        if (c.id !== id) return c;
        const updated = {
          ...c, interactions: [...(c.interactions || []), interaction], lastContact: today,
          nextAction: data.nextAction || c.nextAction,
          nextActionDate: data.nextActionDate || c.nextActionDate,
          contact: data.contactName || c.contact,
          phone: data.phoneCelular || c.phone,
          whatsapp: data.phoneCelular || c.whatsapp,
        };
        if (data.noInterest) {
          updated.stage = "descartado";
          updated.lossReason = "Sem interesse no momento";
          updated.stageHistory = [...(c.stageHistory || []), { from: c.stage, to: "descartado", date: today, note: "Sem interesse" }];
        } else if (c.stage === "novo_lead") {
          updated.stage = "primeiro_contato";
          updated.stageHistory = [...(c.stageHistory || []), { from: "novo_lead", to: "primeiro_contato", date: today, note: "Primeiro contato registrado" }];
        }
        if (retorno === "aguardando" && data.nextAttemptDate) {
          updated.nextAction = "Retornar contato (aguardando retorno)";
          updated.nextActionDate = data.nextAttemptDate;
        }
        return updated;
      }));
      const followDate = data.nextAttemptDate || data.nextActionDate;
      const followAction = data.nextAction || (retorno === "aguardando" ? "Retornar contato" : "");
      if (followAction && followDate) {
        const cl = clients.find((c) => c.id === id);
        setTasks((p) => [...p, {
          id: generateId(), title: followAction, type: "client", refId: id, userId,
          dueDate: followDate, priority: "medium", done: false,
          notes: `Follow-up: ${cl?.company}`,
        }]);
      }
    } else {
      setPartners((p) => p.map((p2) => {
        if (p2.id !== id) return p2;
        const updated = {
          ...p2, interactions: [...(p2.interactions || []), interaction], lastContact: today,
          contact: data.contactName || p2.contact,
          phone: data.phoneCelular || p2.phone,
          // lastContact é o que zera o relógio da cobrança: a espera anterior
          // passa a estar respondida por um ato, não por uma promessa.
        };
        // Quem registrou o contato passa a responder pela empresa. A rede é de
        // todos (a pilha "a contatar" não é de ninguém), mas contato registrado
        // tem dono — senão o card volta para a pilha comum e outra pessoa liga
        // para quem já foi contatado ontem. Só pega o que está solto ou ainda
        // não foi trabalhado; empresa em documentação não troca de mãos por um
        // registro de contato.
        if (userId && p2.userId !== userId && (!p2.userId || p2.stage === "identificado")) {
          updated.userId = userId;
          updated.stageHistory = [...(p2.stageHistory || []), {
            from: p2.stage, to: p2.stage, date: today, autor: userName,
            tipo: "assumido", de: p2.userId || null, para: userId,
            note: `${userName || "Usuário"} assumiu esta empresa ao registrar o contato`,
          }];
        }
        // O número mudou: o próximo contato precisa sair do número certo. O
        // antigo não se perde — fica na trilha e dentro da própria interação.
        if (data.continuaEm) {
          updated.phone = data.continuaEm;
          if (data.continuaCom) updated.contact = data.continuaCom;
          updated.stageHistory = [...(updated.stageHistory || p2.stageHistory || []), {
            from: p2.stage, to: p2.stage, date: today, autor: userName,
            tipo: "continuacao", de: p2.phone || "", para: data.continuaEm,
            note: `Conversa continua em ${data.continuaEm}${data.continuaCom ? ` (${data.continuaCom})` : ""}`,
          }];
        }
        if (semInteresse) {
          updated.stage = "sem_interesse";
          updated.nextAction = "Retomar contato (sem interesse anterior)";
          updated.nextActionDate = data.resumeDate || addMonths(today, 3);
          // O motivo vai DENTRO da trilha: não existe coluna para ele, e solto
          // seria descartado na gravação (some na primeira recarga).
          updated.stageHistory = [...(p2.stageHistory || []), {
            from: p2.stage, to: "sem_interesse", date: today, autor: userName,
            tipo: "sem_interesse", motivo: data.motivoRecusa?.trim() || "",
            note: `Sem interesse: ${data.motivoRecusa?.trim() || "sem motivo"}`,
          }];
        } else if (aceitou && provaAvanco && ["identificado", "abordagem"].includes(p2.stage)) {
          // O aceite vem antes do "primeiro contato": quem aceita já na primeira
          // conversa vai direto para documentação, sem parar em abordagem.
          updated.stage = "documentacao";
          updated.stageHistory = [...(updated.stageHistory || p2.stageHistory || []), { from: p2.stage, to: "documentacao", date: today, note: "Aceitou credenciamento", autor: userName }];
        } else if (p2.stage === "identificado" && provaAvanco) {
          updated.stage = "abordagem";
          updated.stageHistory = [...(updated.stageHistory || p2.stageHistory || []), { from: "identificado", to: "abordagem", date: today, note: "Primeiro contato registrado", autor: userName }];
        }
        if (retorno === "aguardando" && data.nextAttemptDate) {
          updated.nextAction = "Retornar contato (aguardando retorno)";
          updated.nextActionDate = data.nextAttemptDate;
        }
        return updated;
      }));
      const followDate = data.nextAttemptDate || data.nextActionDate;
      const followAction = data.nextAction || (retorno === "aguardando" ? "Retornar contato" : "");
      if (followAction && followDate) {
        const pt = partners.find((p2) => p2.id === id);
        setTasks((p) => [...p, {
          id: generateId(), title: followAction, type: "partner", refId: id, userId,
          dueDate: followDate, priority: "medium", done: false,
          notes: `Follow-up: ${pt?.name}`,
        }]);
      }
    }

    // Se veio de uma tarefa atrasada do painel, conclui essa tarefa
    if (target.taskId) {
      setTasks((p) => p.map((t) => (t.id === target.taskId ? { ...t, done: true } : t)));
    }

    apagarRascunho(RASCUNHO_INTERACAO);
    setData(emptyData());
    setErrors({});
    onClose();

    // "Aceitou credenciar" e o proximo passo e sempre o mesmo: mandar a ficha.
    // Fechar o modal e deixar a pessoa procurar o botao era onde o aceite
    // esfriava — quem acabou de ouvir o sim é quem manda o link.
    if (isPartner && aceitou && provaAvanco && onAceitouCredenciar) {
      const parceiro = partners.find((x) => x.id === id);
      if (parceiro) setTimeout(() => onAceitouCredenciar(parceiro), 120);
    }
  };

  const handleClose = () => {
    apagarRascunho(RASCUNHO_INTERACAO);
    setData(emptyData());
    setErrors({});
    onClose();
  };

  return (
    <Modal open={!!target} onClose={handleClose} title="Registrar Contato / Interação" w={540}>
      <GuideBanner title="Regra de ouro" text="SEMPRE registre o resultado de cada contato E defina o próximo passo com data. Um lead sem próximo passo definido é um lead esquecido." />

      {entity && (
        <div style={{ padding: "8px 12px", borderRadius: 8, background: "#f8fafc", border: "1px solid #e2e8f0", marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
            {isClient ? `${entity.company}` : `${entity.name}`}
          </div>
          <div style={{ fontSize: 11, color: "#64748b" }}>
            {entity.contact ? `Contato: ${entity.contact}` : "Sem contato registrado ainda"}
            {entity.phone ? ` • ${entity.phone}` : ""}
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
        <Field label="Canal de Contato">
          <select style={selectStyle} value={data.channel} onChange={(e) => setData((p) => ({ ...p, channel: e.target.value }))}>
            {CONTACT_CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        {porTelefone && (
          <div style={{ gridColumn: "1/-1" }}>
            <Field label="Número para onde você ligou" required help="Registro telefônico: ligação não tem export — o número chamado, com o seu nome, é o que responde pelo contato" error={errors.telefoneLigacao}>
              <input style={errors.telefoneLigacao ? inputErrorStyle : inputStyle} value={data.telefoneLigacao} onChange={(e) => setData((p) => ({ ...p, telefoneLigacao: maskPhone(e.target.value) }))} placeholder="(00) 00000-0000" />
            </Field>
          </div>
        )}
        {porVisita && (
          <div style={{ gridColumn: "1/-1", marginBottom: 8, padding: "7px 10px", borderRadius: 7, background: provaVisita ? "#f0fdf4" : "#fffbeb", border: `1px solid ${provaVisita ? "#bbf7d0" : "#fde68a"}`, fontSize: 11, color: provaVisita ? "#166534" : "#92400e", lineHeight: 1.45 }}>
            {provaVisita
              ? <>Visita com foto e nome de quem recebeu — <strong>avança o funil</strong>.</>
              : <>Visita presencial <strong>avança o funil</strong> com a <strong>foto da visita</strong> (fachada, cartão ou com o responsável) e o <strong>nome de quem recebeu</strong> em "Nome do Contato". Sem isso, fica só registrada.</>}
          </div>
        )}
        {isPartner && !["WhatsApp", "Telefone", "Visita Presencial"].includes(data.channel) && (
          <div style={{ gridColumn: "1/-1", marginBottom: 8, padding: "7px 10px", borderRadius: 7, background: "#fffbeb", border: "1px solid #fde68a", fontSize: 11, color: "#92400e", lineHeight: 1.45 }}>
            Contato por <strong>{data.channel}</strong> fica registrado, mas <strong>não avança o funil</strong>. Para andar na Rede é preciso o export da conversa do WhatsApp, o registro telefônico com o número ou a visita presencial com foto.
          </div>
        )}
        <Field label="Nome do Contato" help="Com quem falou/tentou falar — o export preenche sozinho">
          <input style={inputStyle} value={data.contactName} onChange={(e) => setData((p) => ({ ...p, contactName: e.target.value }))} placeholder="Ex: José Carlos" />
        </Field>
        <Field label="Celular">
          <input style={inputStyle} value={data.phoneCelular} onChange={(e) => setData((p) => ({ ...p, phoneCelular: maskPhone(e.target.value) }))} placeholder="(11) 99999-0000" />
        </Field>
        <Field label="Telefone Fixo">
          <input style={inputStyle} value={data.phoneFixo} onChange={(e) => setData((p) => ({ ...p, phoneFixo: maskPhone(e.target.value) }))} placeholder="(11) 3333-0000" />
        </Field>

        {/* ── Comprovante do contato ──────────────────────────────
            Sem prova, "abordagem" era só uma palavra no card: ninguém
            conseguia saber se a conversa aconteceu, quando, nem com quem.
            A conversa exportada — ou, na falta dela, um print com data e
            hora visíveis — é o que faz o registro virar trilha. */}
        {(isPartner || isClient) && (
          <div style={{ gridColumn: "1/-1", marginTop: 4, marginBottom: 4 }}>
            <div style={{
              padding: "11px 13px", borderRadius: 9,
              background: data.comprovantePath ? "#f0fdfa" : (errors.comprovante ? "#fef2f2" : "#f8fafc"),
              border: `1px solid ${data.comprovantePath ? "#5eead4" : (errors.comprovante ? "#fecaca" : "#e2e8f0")}`,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: "#0f172a", marginBottom: 3 }}>
                <Camera size={13} color="#7c3aed" />
                Comprovante da conversa {printObrigatorio ? <span style={{ color: "#dc2626" }}>*</span> : <span style={{ color: "#94a3b8", fontWeight: 600 }}>(opcional neste canal)</span>}
              </div>
              <div style={{ fontSize: 10.5, color: "#64748b", lineHeight: 1.45, marginBottom: 8 }}>
                {printObrigatorio
                  ? "Exporte a conversa na plataforma (botão \"export\") e anexe o arquivo — vem a conversa inteira, com hora real e assinatura, e ele já identifica com quem você falou. Print não basta para avançar na Rede."
                  : "Anexe a conversa exportada, o print, a foto ou o e-mail. Contato sem comprovante fica marcado no histórico."}
              </div>

              {data.comprovantePath ? (
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 5, padding: "5px 10px", borderRadius: 6, background: "#fff", border: "1px solid #5eead4", fontSize: 11, color: "#0f766e", fontWeight: 600, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    <Paperclip size={11} /> {data.comprovanteNome}
                  </span>
                  <button type="button" onClick={verPrint} style={{ padding: "5px 10px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#475569", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>Ver</button>
                  {/* Juntar outro export: a plataforma exporta a conversa toda,
                      então o próximo arquivo é mesclado por cima e as linhas
                      repetidas caem fora. Só faz sentido para conversa. */}
                  {data.comprovanteTipo === "conversa" && (
                    <label style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 6, border: "1px solid #99f6e4", background: "#fff", color: "#0f766e", fontSize: 11, fontWeight: 600, cursor: enviandoPrint ? "wait" : "pointer" }}>
                      <Paperclip size={11} /> {enviandoPrint ? "Juntando..." : "Juntar outro export"}
                      <input type="file" accept="application/json,.json" disabled={enviandoPrint}
                        onChange={(e) => { anexarPrint(e.target.files?.[0]); e.target.value = ""; }} style={{ display: "none" }} />
                    </label>
                  )}
                  <button type="button" onClick={() => setData((p) => ({ ...p, comprovantePath: "", comprovanteNome: "", comprovanteTipo: "", conversaResumo: null, conversa: null }))} style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 10px", borderRadius: 6, border: "1px solid #fecaca", background: "#fff", color: "#b91c1c", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                    <XIcon size={11} /> Trocar
                  </button>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {/* Visita: botão que abre a CÂMERA. Sem `capture`, o Android
                    só oferece o Google Fotos. */}
                {porVisita && cameraNoSiteDisponivel() && (
                  // Câmera na própria página — o app de câmera fazia o Android
                  // fechar o navegador e a aba voltava recarregada.
                  <>
                    <button type="button" disabled={enviandoPrint} onClick={() => setCamera(true)}
                      style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 13px", borderRadius: 7, border: "1px solid #d97706", background: "#fffbeb", color: "#b45309", fontSize: 11.5, fontWeight: 700, cursor: enviandoPrint ? "wait" : "pointer" }}>
                      <Camera size={12} /> {enviandoPrint ? "Enviando..." : "Tirar foto agora"}
                    </button>
                    <CameraCaptura aberta={camera} onFechar={() => setCamera(false)} onFoto={anexarPrint} />
                  </>
                )}
                {porVisita && !cameraNoSiteDisponivel() && (
                  <label style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 13px", borderRadius: 7, border: "1px solid #d97706", background: "#fffbeb", color: "#b45309", fontSize: 11.5, fontWeight: 700, cursor: enviandoPrint ? "wait" : "pointer" }}>
                    <Camera size={12} /> {enviandoPrint ? "Enviando..." : "Tirar foto agora"}
                    <input type="file" accept="image/*" capture="environment" disabled={enviandoPrint}
                      onChange={(e) => { anexarPrint(e.target.files?.[0]); e.target.value = ""; }} style={{ display: "none" }} />
                  </label>
                )}
                <label style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 13px", borderRadius: 7, border: "1px dashed #94a3b8", background: "#fff", color: "#475569", fontSize: 11.5, fontWeight: 600, cursor: enviandoPrint ? "wait" : "pointer" }}>
                  <Paperclip size={12} />
                  {porVisita ? "Escolher da galeria" : enviandoPrint ? "Enviando..." : "Anexar conversa exportada ou print"}
                  <input
                    type="file" accept={porVisita ? "image/*" : "image/*,application/pdf,application/json,.json"} disabled={enviandoPrint}
                    onChange={(e) => { anexarPrint(e.target.files?.[0]); e.target.value = ""; }}
                    style={{ display: "none" }}
                  />
                </label>
                </div>
              )}

              {errors.comprovante && (
                <div style={{ fontSize: 10.5, color: "#dc2626", marginTop: 6, fontWeight: 600 }}>{errors.comprovante}</div>
              )}

              {data.conversaResumo && (
                <div style={{ marginTop: 8, padding: "8px 10px", borderRadius: 7, background: "#f0fdfa", border: "1px solid #99f6e4", fontSize: 10.5, color: "#0f766e", lineHeight: 1.5 }}>
                  <strong>Conversa com {data.conversaResumo.contato}</strong>
                  {data.conversaResumo.numero ? ` (${data.conversaResumo.numero})` : ""} — {descreverConversa(data.conversaResumo)}.
                  {data.conversaResumo.atendente ? ` Atendida por ${data.conversaResumo.atendente}.` : ""}
                  {data.conversaResumo.mesclado
                    ? ` Consolidado de ${data.conversaResumo.fontes} exports (linhas repetidas descartadas).${data.conversaResumo.todasAssinadas ? " Cada parte veio assinada da plataforma." : " Confira a origem das partes."}`
                    : (data.conversaResumo.assinado
                      ? " Arquivo assinado pela plataforma."
                      : " Atenção: arquivo sem assinatura.")}
                </div>
              )}

              {isPartner && !printObrigatorio && !data.comprovantePath && (
                <div style={{ marginTop: 8 }}>
                  <input
                    value={data.motivoSemPrint || ""}
                    onChange={(e) => setData((p) => ({ ...p, motivoSemPrint: e.target.value }))}
                    placeholder="Por que não há comprovante? Ex: visita presencial, ligação sem registro"
                    style={{ width: "100%", padding: "7px 10px", borderRadius: 6, border: `1px solid ${errors.motivoSemPrint ? "#ef4444" : "#d1d5db"}`, fontSize: 11.5, boxSizing: "border-box", background: "#fff", color: "#334155", fontFamily: "inherit" }}
                  />
                  {errors.motivoSemPrint && <div style={{ fontSize: 10.5, color: "#dc2626", marginTop: 4, fontWeight: 600 }}>{errors.motivoSemPrint}</div>}
                </div>
              )}

              <div style={{ display: "flex", alignItems: "flex-start", gap: 5, fontSize: 9.5, color: "#64748b", marginTop: 8, lineHeight: 1.4 }}>
                <ShieldCheck size={11} color="#059669" style={{ flexShrink: 0, marginTop: 1 }} />
                O comprovante fica em servidor privado, criptografado, e só a equipe com acesso ao CRM consegue abrir.
              </div>
            </div>
          </div>
        )}

        {/* No que deu esta conversa. Fica logo abaixo do anexo porque é a
            pergunta que a conversa levanta — e "aguardando" é resposta legítima,
            desde que venha com data. */}
        {isPartner && (
          <div style={{ gridColumn: "1/-1", marginTop: 4 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a", marginBottom: 6 }}>
              No que deu? {data.comprovanteTipo === "conversa" && <span style={{ color: "#dc2626" }}>*</span>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 6 }}>
              {[
                { id: "aguardando", label: "Aguardando resposta", cor: "#d97706", dica: "Mandou e ainda não respondeu — agende a próxima tentativa" },
                { id: "aceitou", label: "Aceitou credenciar", cor: "#0d9488", dica: "Vai para Documentação" },
                { id: "sem_retorno", label: "Sem retorno", cor: "#64748b", dica: "Tentou e não houve conversa" },
                { id: "sem_interesse", label: "Sem interesse", cor: "#dc2626", dica: "Sai do funil — o motivo é obrigatório" },
              ].map((op) => (
                <button
                  key={op.id} type="button" title={op.dica}
                  onClick={() => setData((p) => ({
                    ...p,
                    resultado: p.resultado === op.id ? "" : op.id,
                    noInterest: op.id === "sem_interesse" ? p.resultado !== op.id : false,
                    resumeDate: op.id === "sem_interesse" && !p.resumeDate ? addMonths(getToday(), 3) : p.resumeDate,
                    nextAttemptDate: op.id === "aguardando" && !p.nextAttemptDate ? "" : p.nextAttemptDate,
                  }))}
                  style={{
                    padding: "9px 10px", borderRadius: 8, textAlign: "left", cursor: "pointer", fontSize: 11.5,
                    border: `1px solid ${data.resultado === op.id ? op.cor : "#e2e8f0"}`,
                    background: data.resultado === op.id ? `${op.cor}12` : "#fff",
                    color: data.resultado === op.id ? op.cor : "#475569",
                    fontWeight: data.resultado === op.id ? 700 : 600,
                  }}
                >
                  {op.label}
                </button>
              ))}
            </div>
            {errors.resultado && <div style={{ fontSize: 10.5, color: "#dc2626", marginTop: 5, fontWeight: 600 }}>{errors.resultado}</div>}

            {/* O que o sistema vai fazer com a resposta, dito antes de salvar. */}
            {data.resultado === "aceitou" && (
              <div style={{ marginTop: 8, padding: "8px 10px", borderRadius: 8, background: "#f0fdfa", border: "1px solid #99f6e4", fontSize: 11.5, color: "#0f766e", lineHeight: 1.45 }}>
                Ao salvar, o parceiro vai para <strong>Documentação</strong> e abre o envio do
                <strong> formulário de credenciamento</strong> já vinculado a ele — é só escolher WhatsApp ou e-mail.
              </div>
            )}
            {data.resultado === "sem_retorno" && (
              <div style={{ marginTop: 8, padding: "8px 10px", borderRadius: 8, background: "#f8fafc", border: "1px solid #e2e8f0", fontSize: 11.5, color: "#475569", lineHeight: 1.45 }}>
                Fica registrado como tentativa. Marque <strong>Aguardando resposta</strong> se quiser que ele volte ao seu Painel do Dia numa data.
              </div>
            )}

            {data.resultado === "aguardando" && (
              <div style={{ marginTop: 8 }}>
                <Field label="Quando você tenta de novo? *" help="Vira tarefa no Painel do Dia nessa data — enquanto não houver resposta, este parceiro fica sinalizado como pendente" error={errors.nextAttemptDate}>
                  <input
                    style={errors.nextAttemptDate ? inputErrorStyle : inputStyle}
                    type="date" value={data.nextAttemptDate}
                    onChange={(e) => setData((p) => ({ ...p, nextAttemptDate: e.target.value }))}
                  />
                </Field>
              </div>
            )}

            {/* Continuação — vale para qualquer desfecho, inclusive "sem
                interesse" (quem atendeu não decide) e "sem retorno" (o número
                pode estar errado). É o que faz a próxima tentativa sair do
                número certo em vez de repetir a que não deu em nada. */}
            {!!data.resultado && (
              <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 8, background: "#f8fafc", border: `1px solid ${errors.continuaEm ? "#fecaca" : "#e2e8f0"}` }}>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: "#0f172a", marginBottom: 2 }}>
                  Indicaram outro número para falar? <span style={{ fontWeight: 600, color: "#94a3b8" }}>(opcional)</span>
                </div>
                <div style={{ fontSize: 10.5, color: "#64748b", lineHeight: 1.45, marginBottom: 8 }}>
                  O contato passa a ser este número — o anterior fica guardado no histórico, e o WhatsApp do card abre o novo.
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
                  <Field label="Continuar em (WhatsApp/telefone)" error={errors.continuaEm}>
                    <input
                      style={errors.continuaEm ? inputErrorStyle : inputStyle}
                      value={data.continuaEm}
                      onChange={(e) => setData((p) => ({ ...p, continuaEm: maskPhone(e.target.value) }))}
                      placeholder="(00) 00000-0000"
                    />
                  </Field>
                  <Field label="Com quem falar lá">
                    <input
                      style={inputStyle} value={data.continuaCom}
                      onChange={(e) => setData((p) => ({ ...p, continuaCom: e.target.value }))}
                      placeholder="Ex: o dono, o financeiro"
                    />
                  </Field>
                </div>
              </div>
            )}
          </div>
        )}

        {isPartner && (
          <div style={{ gridColumn: "1/-1" }}>
            <Field label="Data e hora do contato" required help="Precisa bater com o que aparece no comprovante" error={errors.contatoEm}>
              <input
                style={errors.contatoEm ? inputErrorStyle : inputStyle}
                type="datetime-local" value={data.contatoEm}
                max={agoraLocal()}
                onChange={(e) => setData((p) => ({ ...p, contatoEm: e.target.value }))}
              />
            </Field>
          </div>
        )}

        <div style={{ gridColumn: "1/-1", marginTop: 4 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 12, cursor: "pointer", color: "#475569" }}>
            <input type="checkbox" checked={data.hadCall} onChange={(e) => setData((p) => ({ ...p, hadCall: e.target.checked }))} />
            Houve ligação telefônica?
          </label>
        </div>

        {data.hadCall && (
          <Field label="Com quem falou na ligação?">
            <input style={inputStyle} value={data.spokeWith} onChange={(e) => setData((p) => ({ ...p, spokeWith: e.target.value }))} placeholder="Ex: Recepcionista, Gerente..." />
          </Field>
        )}
        <div style={{ gridColumn: data.hadCall ? "1/2" : "1/-1" }}>
          <Field label="Devolução / Resposta">
            <input style={inputStyle} value={data.feedback} onChange={(e) => setData((p) => ({ ...p, feedback: e.target.value }))} placeholder="Qual foi a resposta?" />
          </Field>
        </div>

        {!isPartner && (data.channel === "WhatsApp" || data.channel === "Telefone") && (
          <>
            <div style={{ gridColumn: "1/-1", marginTop: 4 }}>
              <Field label="Houve retorno?">
                <div style={{ display: "flex", gap: 8 }}>
                  {[
                    { value: "sim", label: "Retorno imediato", bg: "#f0fdfa", border: "#10b981" },
                    { value: "aguardando", label: "Aguardando", bg: "#fffbeb", border: "#f59e0b" },
                    { value: "sem_retorno", label: "Sem retorno", bg: "#fef2f2", border: "#ef4444" },
                  ].map((opt) => (
                    <div key={opt.value} onClick={() => setData((p) => ({ ...p, immediateReturn: p.immediateReturn === opt.value ? "" : opt.value }))} style={{
                      flex: 1, padding: "8px 6px", borderRadius: 6, cursor: "pointer", textAlign: "center",
                      background: data.immediateReturn === opt.value ? opt.bg : "#f8fafc",
                      border: `2px solid ${data.immediateReturn === opt.value ? opt.border : "#e2e8f0"}`,
                      fontSize: 11, fontWeight: data.immediateReturn === opt.value ? 600 : 400,
                      color: data.immediateReturn === opt.value ? opt.border : "#64748b",
                    }}>
                      {opt.label}
                    </div>
                  ))}
                </div>
              </Field>
            </div>
            {data.immediateReturn === "aguardando" && (
              <div style={{ gridColumn: "1/-1" }}>
                <Field label="Data da próxima tentativa" help="Quando tentar novamente?">
                  <input style={inputStyle} type="date" value={data.nextAttemptDate} onChange={(e) => setData((p) => ({ ...p, nextAttemptDate: e.target.value }))} />
                </Field>
              </div>
            )}
          </>
        )}

        <div style={{ gridColumn: "1/-1", marginTop: 4 }}>
          <label style={{
            display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 6, cursor: "pointer", fontSize: 12,
            background: data.noInterest ? "#fef2f2" : "#f8fafc",
            border: `1px solid ${data.noInterest ? "#ef4444" : "#e2e8f0"}`,
            color: data.noInterest ? "#991b1b" : "#64748b",
          }}>
            <input type="checkbox" checked={data.noInterest} onChange={(e) => setData((p) => ({
              ...p,
              noInterest: e.target.checked,
              resumeDate: e.target.checked && isPartner && !p.resumeDate ? addMonths(getToday(), 3) : p.resumeDate,
            }))} />
            {isPartner
              ? "Sem interesse agora — guardar para retomar depois"
              : "Não teve interesse — marcar e armazenar"}
          </label>
        </div>

        {isPartner && data.noInterest && (
          <div style={{ gridColumn: "1/-1" }}>
            <Field label="Por que não seguiu? *" help="Fica no histórico e é o que orienta a retomada" error={errors.motivoRecusa}>
              <input
                style={errors.motivoRecusa ? inputErrorStyle : inputStyle}
                value={data.motivoRecusa || ""}
                onChange={(e) => setData((p) => ({ ...p, motivoRecusa: e.target.value }))}
                placeholder="Ex: já atende por outra rede, achou a taxa alta, vai avaliar em 2026"
              />
            </Field>
          </div>
        )}

        {isPartner && data.noInterest && (
          <div style={{ gridColumn: "1/-1" }}>
            <Field label="Quando retomar o contato?" help="O parceiro reaparece no Painel do Dia nesta data">
              <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                <input style={{ ...inputStyle, width: "auto" }} type="date" value={data.resumeDate} onChange={(e) => setData((p) => ({ ...p, resumeDate: e.target.value }))} />
                {[1, 2, 3, 6].map((m) => (
                  <button key={m} type="button" onClick={() => setData((p) => ({ ...p, resumeDate: addMonths(getToday(), m) }))} style={{
                    padding: "5px 10px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#f8fafc",
                    color: "#475569", fontSize: 11, fontWeight: 600, cursor: "pointer",
                  }}>
                    {m} {m === 1 ? "mês" : "meses"}
                  </button>
                ))}
              </div>
            </Field>
          </div>
        )}

        {isPartner && entity?.stage === "abordagem" && !data.noInterest && (
          <div style={{ gridColumn: "1/-1", marginTop: 4 }}>
            <label style={{
              display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 6, cursor: "pointer", fontSize: 12,
              background: data.acceptedCredential ? "#f0fdfa" : "#f8fafc",
              border: `1px solid ${data.acceptedCredential ? "#059669" : "#e2e8f0"}`,
              color: data.acceptedCredential ? "#065f46" : "#64748b",
            }}>
              <input type="checkbox" checked={data.acceptedCredential} onChange={(e) => setData((p) => ({ ...p, acceptedCredential: e.target.checked }))} />
              Aceitou credenciar — mover para Documentação
            </label>
          </div>
        )}

        <div style={{ gridColumn: "1/-1", marginTop: 6 }}>
          <Field label="O que aconteceu neste contato?" required error={errors.note}>
            <textarea style={{ ...(errors.note ? inputErrorStyle : inputStyle), minHeight: 60, resize: "vertical" }} value={data.note} onChange={(e) => setData((p) => ({ ...p, note: e.target.value }))} placeholder="Descreva o resultado do contato..." />
          </Field>
        </div>

        {!data.noInterest && data.immediateReturn !== "aguardando" && (
          <>
            <Field label="Próximo Passo">
              <input style={inputStyle} value={data.nextAction} onChange={(e) => setData((p) => ({ ...p, nextAction: e.target.value }))} placeholder="Ex: Enviar proposta..." />
            </Field>
            <Field label="Data do Próximo Passo">
              <input style={inputStyle} type="date" value={data.nextActionDate} onChange={(e) => setData((p) => ({ ...p, nextActionDate: e.target.value }))} />
            </Field>
          </>
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginTop: 10 }}>
        <button onClick={handleClose} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Cancelar</button>
        <button onClick={handleSubmit} disabled={enviandoPrint} style={{
          padding: "7px 14px", borderRadius: 6, border: "none", fontSize: 12, fontWeight: 600,
          cursor: enviandoPrint ? "wait" : "pointer", opacity: enviandoPrint ? 0.6 : 1,
          background: data.noInterest ? "#ef4444" : "#0d9488", color: "#fff",
        }}>
          {enviandoPrint ? "Enviando comprovante..." : data.noInterest ? "Registrar Sem Interesse" : "Registrar Contato"}
        </button>
      </div>
    </Modal>
  );
}
